import type { ReactElement } from 'react';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

export interface VolumeRunRow {
  id: string;
  contractId: string;
  wellId: string;
  periodId: string;
  product: string;
  productionDate: string;
  gross: number;
  unit: string;
  settlementPrice?: number | null;
  reversedFromId?: string | null;
  gravity?: number | null;
  settlementFormulaName?: string | null;
  lines: { partyId: string; interestClass: string; decimal: string; allocatedQuantity: number }[];
}

const COLUMNS: GroupedColumnDef[] = [
  { id: 'periodId', label: 'Period', header: 'Period' },
  { id: 'product', label: 'Product', header: 'Product' },
  { id: 'gross', label: 'Gross', header: 'Gross' },
  { id: 'productionDate', label: 'Production date', header: 'Production date' },
  { id: 'allocations', label: 'Allocations', header: 'Allocations' }
];

function groupValue(row: VolumeRunRow, columnId: string): string {
  switch (columnId) {
    case 'periodId':
      return row.periodId;
    case 'product':
      return row.product;
    case 'gross':
      return `${row.gross} ${row.unit}`;
    case 'productionDate':
      return row.productionDate;
    case 'allocations':
      return String(row.lines.length);
    default:
      return '—';
  }
}

interface VolumeRunsGridProps {
  rows: VolumeRunRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  onNew?: () => void;
  loading?: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function VolumeRunsGrid({
  rows,
  selectedId,
  onSelect,
  onOpen,
  onNew,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}: VolumeRunsGridProps): ReactElement {
  return (
    <section className="card requests-grid-card">
      <div className="row spread requests-grid-toolbar">
        <h2>Volume runs</h2>
        {onNew && (
          <button type="button" onClick={onNew} data-testid="volume-new">
            + Post volume
          </button>
        )}
      </div>
      <p className="hint">Click a row for the side panel; double-click to open the full page.</p>
      {loading && <p className="hint">Loading volume runs…</p>}
      <GroupedDataGrid<VolumeRunRow>
        columns={COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={groupValue}
        colSpan={5}
        emptyMessage="No volume runs match this product."
        groupHint="Click a column title to sort. Drag the grip beside a title into the strip to group."
        renderRow={(row) => (
          <tr
            className={selectedId === row.id ? 'selected' : undefined}
            data-testid="volume-run-row"
            onClick={() => onSelect(row.id)}
            onDoubleClick={() => onOpen(row.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(row.id);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <td className="mono-id">{row.periodId}</td>
            <td>
              <span className="status-pill">{row.product}</span>
            </td>
            <td>
              {row.gross} {row.unit}
            </td>
            <td>{row.productionDate}</td>
            <td>{row.lines.length} owners</td>
          </tr>
        )}
      />
      <GridPaginationBar
        idPrefix="volume-runs"
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
  );
}
